<template>
  <div class="crm-wrapper">
    <!-- MAIN -->
    <main class="crm-main">
      <h1 class="">CRM Command Center</h1>
      <p class="page-subtitle">A live view of activity, pipeline health, and revenue movement.</p>

      <!-- LOADING STATE -->
      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-primary" role="status">
          <!-- <span class="visually-hidden">Loading...</span> -->
        </div>
        <span class="loading-text">Loading dashboard…</span>
      </div>

      <!-- ERROR STATE -->
      <div v-else-if="error" class="alert alert-danger d-flex align-items-center gap-2" role="alert">
        <i class="fa fa-exclamation-circle"></i>
        <span>Failed to load dashboard data. Please try again.</span>
      </div>

      <template v-else>

        <!-- STAT CARDS -->
        <div class="row g-3 mb-4">
          <div class="col-6 col-md-4 col-lg-2" v-for="stat in computedStats" :key="stat.label">
            <div class="stat-card">
              <span :class="['stat-change', stat.dir]">
                <i :class="stat.icon_change"></i>
                {{ stat.change }}
              </span>
              <div :class="['stat-icon-wrap', stat.iconBg]">
                <i :class="`${stat.icon}`"></i>
              </div>
              <div class="stat-label">{{ stat.label }}</div>
              <div class="stat-value">{{ stat.value }}</div>
            </div>
          </div>
        </div>

        <!-- BOTTOM PANELS -->
        <div class="row g-3">

          <!-- TODAY'S ACTIVITY -->
          <div class="col-12 col-lg-4">
            <div class="panel-card">
              <div class="panel-header">
                <span class="panel-title">Today's Activity</span>
                <!-- <a class="panel-link">View report</a> -->
              </div>
              <div class="panel-body">
                <div v-if="!activities_list" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
                <template v-else>
                  <div class="activity-item" v-for="act in computedActivities" :key="act.name">
                    <div :class="['activity-icon', act.iconBg]">
                      <i :class="[act.icon]"></i>
                    </div>
                    <div class="activity-info">
                      <div class="activity-name">{{ act.name }}</div>
                      <div class="activity-sub">Updated live</div>
                    </div>
                    <div class="activity-count">{{ act.count }}</div>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- UPCOMING WORK -->
          <div class="col-12 col-lg-4">
            <div class="panel-card">
              <div class="panel-header">
                <span class="panel-title">Upcoming Work</span>
                <!-- <a class="panel-link">Open leads</a> -->
              </div>
              <div class="panel-body">
                <div v-if="!upcomingWorks || upcomingWorks.length === 0" class="empty-state">
                  <i class="fa fa-calendar-check-o empty-icon"></i>
                  <p class="empty-text">No upcoming work scheduled.</p>
                </div>
                <template v-else>
                  <div v-for="work in upcomingWorks" :key="work.lead_id">
                    <router-link :to="`/crm/lead/details/${work.lead_id}`" class="work-item">
                            <div class="work-info">
                      <div class="work-title">{{ work.next_action }}</div>
                      <div class="work-meta">
                        {{ work.company }}
                        <span class="meta-dot">·</span>
                        {{ work.owner }}
                        <span class="meta-dot">·</span>
                        <span class="due-date">{{ formatDate(work.due_date) }}</span>
                      </div>
                    </div>
                    <span :class="['priority-badge', (work.priority || '').toLowerCase()]">
                      {{ capitalize(work.priority) }}
                    </span>
                    </router-link>
                    
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- PIPELINE BY STAGE -->
          <div class="col-12 col-lg-4">
            <div class="panel-card">
              <div class="panel-header">
                <span class="panel-title">Pipeline by Stage</span>
                <!-- <a class="panel-link">Deal room</a> -->
              </div>
              <div class="panel-body">
                <div v-if="!pipeline_list || pipeline_list.length === 0" class="empty-state">
                  <i class="fa fa-bar-chart empty-icon"></i>
                  <p class="empty-text">No pipeline data available.</p>
                </div>
                <template v-else>
                  <div class="pipeline-item" v-for="stage in computedPipeline" :key="stage.stage">
                    <div class="pipeline-row">
                      <span class="pipeline-stage">{{ capitalize(stage.stage) }}</span>
                      <span class="pipeline-count">{{ stage.count }}</span>
                    </div>
                    <div class="pipeline-bar-track">
                      <div
                        class="pipeline-bar-fill"
                        :style="{ width: barWidth(stage.count) + '%', background: stage.color }"
                      ></div>
                    </div>
                    <div v-if="stage.total_value > 0" class="pipeline-value">
                      ৳{{ formatCurrency(stage.total_value) }}
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>

        </div>
      </template>
    </main>

  </div>
</template>

<script>
import { mapState } from "vuex"

const STAGE_COLORS = {
  suspect:   "#2563eb",
  qualified: "#06b6d4",
  discovery: "#8b5cf6",
  demo:      "#22c55e",
  proposal:  "#f97316",
  trial:     "#ec4899",
  customer:  "#a855f7",
}

export default {
  name: "CRMDashboard",

  data() {
    return {
      searchQuery: "",
    }
  },

  mounted() {
    this.$store.dispatch("crmDashboard/fetchCRMDashboardStats")
    this.$store.dispatch("crmDashboard/fetchCRMDashboardActivity")
    this.$store.dispatch("crmDashboard/fetchCRMDashboardUpcomingWork")
    this.$store.dispatch("crmDashboard/fetchCRMDashboardPipeline")
  },

  computed: {
    ...mapState({
      stats_list:      state => state.crmDashboard.stats,
      activities_list: state => state.crmDashboard.activty,
      upcomingWorks:   state => state.crmDashboard.upcomingWorks,
      pipeline_list:   state => state.crmDashboard.pipelines,
      loading:         state => state.crmDashboard.submitLoading,
      error:           state => state.crmDashboard.submitError,
    }),

    pipelineMax() {
      if (!this.pipeline_list || this.pipeline_list.length === 0) return 1
      return Math.max(...this.pipeline_list.map(s => s.count)) || 1
    },

    computedStats() {
      const s = this.stats_list || {}
      const stageCounts = s.stage_counts || {}
      const totalLeads = Object.values(stageCounts).reduce((a, b) => a + b, 0)

      return [
        {
          label:       "Pipeline Value",
          value:       "৳" + this.formatCurrency(s.pipeline_value || 0),
          change:      "",
          dir:         "neutral",
          icon_change: "fa fa-dollar",
          icon:        "fa fa-dollar",
          iconBg:      "icon-bg-green",
          iconColor:   "icon-green",
        },
        {
          label:       "Total Leads",
          value:       totalLeads,
          change:      "",
          dir:         "neutral",
          icon_change: "fa fa-users",
          icon:        "fa fa-users",
          iconBg:      "icon-bg-blue",
          iconColor:   "icon-blue",
        },
        {
          label:       "Pending Follow-ups",
          value:       s.pending_followups ?? 0,
          change:      s.pending_followups > 0 ? "Action needed" : "All clear",
          dir:         s.pending_followups > 0 ? "down" : "up",
          icon_change: s.pending_followups > 0 ? "fa fa-arrow-down" : "fa fa-check-circle",
          icon:        "fa fa-clock-o",
          iconBg:      "icon-bg-cyan",
          iconColor:   "icon-cyan",
        },
        {
          label:       "Aging Alerts",
          value:       s.aging_alerts ?? 0,
          change:      s.aging_alerts > 0 ? "Needs review" : "All clear",
          dir:         s.aging_alerts > 0 ? "warn" : "up",
          icon_change: s.aging_alerts > 0 ? "fa fa-exclamation-triangle" : "fa fa-check-circle",
          icon:        "fa fa-exclamation-triangle",
          iconBg:      "icon-bg-orange",
          iconColor:   "icon-orange",
        },
        {
          label:       "Quality Review",
          value:       s.quality_review ?? 0,
          change:      s.quality_review > 0 ? "Needs review" : "All clear",
          dir:         s.quality_review > 0 ? "warn" : "up",
          icon_change: s.quality_review > 0 ? "fa fa-exclamation-circle" : "fa fa-check-circle",
          icon:        "fa fa-clipboard",
          iconBg:      "icon-bg-purple",
          iconColor:   "icon-purple",
        },
        {
          label:       "Customers",
          value:       stageCounts.customer ?? 0,
          change:      "",
          dir:         "neutral",
          icon_change: "fa fa-star",
          icon:        "fa fa-star",
          iconBg:      "icon-bg-red",
          iconColor:   "icon-red",
        },
      ]
    },

    computedActivities() {
      const a = this.activities_list || {}
      return [
        {
          name:      "Calls completed",
          count:     a.calls_today ?? 0,
          icon:      "fa fa-phone",
          iconBg:    "icon-bg-red",
          iconColor: "icon-red",
        },
        {
          name:      "Emails sent",
          count:     a.emails_sent ?? 0,
          icon:      "fa fa-envelope",
          iconBg:    "icon-bg-blue",
          iconColor: "icon-blue",
        },
        {
          name:      "Meetings held",
          count:     a.meetings_held ?? 0,
          icon:      "fa fa-calendar",
          iconBg:    "icon-bg-purple",
          iconColor: "icon-purple",
        },
        {
          name:      "Follow-ups done",
          count:     a.followups_done ?? 0,
          icon:      "fa fa-check-square-o",
          iconBg:    "icon-bg-cyan",
          iconColor: "icon-cyan",
        },
      ]
    },

    computedPipeline() {
      if (!this.pipeline_list) return []
      return this.pipeline_list.map(item => ({
        ...item,
        color: STAGE_COLORS[item.stage] || "#64748b",
      }))
    },
  },

  methods: {
    barWidth(count) {
      return Math.round((count / this.pipelineMax) * 100)
    },

    formatCurrency(value) {
      if (!value) return "0"
      if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M"
      if (value >= 1_000)     return (value / 1_000).toFixed(1) + "K"
      return value.toLocaleString()
    },

    formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    },

    capitalize(str) {
      if (!str) return ""
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════
   WRAPPER
══════════════════════════════ */
.crm-wrapper {
  background: #f8fafc;
  color: #0f172a;
}



/* ══════════════════════════════
   MAIN
══════════════════════════════ */
.crm-main {
  padding: 32px 28px 24px;
  margin: 0 auto;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 4px;
  animation: fadeUp 0.45s ease both;
}

.page-subtitle {
  color: #64748b;
  font-size: 13.5px;
  margin-bottom: 28px;
  animation: fadeUp 0.45s 0.08s ease both;
}

/* ══════════════════════════════
   LOADING / ERROR
══════════════════════════════ */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 80px 0;
}

.loading-text {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

/* ══════════════════════════════
   STAT CARDS
══════════════════════════════ */
.stat-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
  animation: fadeUp 0.45s ease both;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 24px rgba(37, 99, 235, 0.12);
}

.stat-change {
  position: absolute;
  top: 18px;
  right: 14px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 3px;
  max-width: 95px;
  text-align: right;
  line-height: 1.3;
}

.stat-change.up      { color: #22c55e; }
.stat-change.down    { color: #ef4444; }
.stat-change.warn    { color: #f97316; }
.stat-change.neutral { color: #94a3b8; }

.stat-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-bottom: 14px;
}

.stat-label {
  font-size: 12.5px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1;
}

/* ══════════════════════════════
   ICON UTILITIES
══════════════════════════════ */
.icon-bg-red    { background: #fef2f2; }
.icon-bg-blue   { background: #eff6ff; }
.icon-bg-purple { background: #faf5ff; }
.icon-bg-cyan   { background: #ecfeff; }
.icon-bg-green  { background: #f0fdf4; }
.icon-bg-orange { background: #fff7ed; }

.icon-red    { color: #ef4444; }
.icon-blue   { color: #2563eb; }
.icon-purple { color: #a855f7; }
.icon-cyan   { color: #06b6d4; }
.icon-green  { color: #22c55e; }
.icon-orange { color: #f97316; }

/* ══════════════════════════════
   PANEL CARDS
══════════════════════════════ */
.panel-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.06);
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: fadeUp 0.45s 0.2s ease both;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 22px 0;
}

.panel-title { font-size: 15px; font-weight: 700; }

.panel-link {
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
  text-decoration: none;
  cursor: pointer;
}

.panel-link:hover { text-decoration: underline; }

.panel-body {
  padding: 16px 22px 20px;
  flex: 1;
}

/* ══════════════════════════════
   ACTIVITY LIST
══════════════════════════════ */
.activity-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 0;
  border-bottom: 1px solid #f1f5f9;
}

.activity-item:last-child { border-bottom: none; }

.activity-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}

.activity-info { flex: 1; }

.activity-name {
  font-size: 13.5px;
  font-weight: 600;
  margin-bottom: 2px;
}

.activity-sub {
  font-size: 11.5px;
  color: #94a3b8;
}

.activity-count {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

/* ══════════════════════════════
   UPCOMING WORK
══════════════════════════════ */
.work-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 0;
  border-bottom: 1px solid #f1f5f9;
}

.work-item:last-child { border-bottom: none; }

.work-info { flex: 1; }

.work-title {
  font-size: 13.5px;
  font-weight: 600;
  margin-bottom: 3px;
}

.work-meta {
  font-size: 11.5px;
  color: #94a3b8;
}

.meta-dot { margin: 0 3px; }

.due-date {
  color: #64748b;
  font-weight: 500;
}

.priority-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1.5px solid;
  white-space: nowrap;
  flex-shrink: 0;
}

.priority-badge.high {
  color: #22c55e;
  border-color: #22c55e;
  background: #f0fdf4;
}

.priority-badge.medium {
  color: #f97316;
  border-color: #f97316;
  background: #fff7ed;
}

.priority-badge.low {
  color: #64748b;
  border-color: #cbd5e1;
  background: #f8fafc;
}

/* ══════════════════════════════
   PIPELINE BARS
══════════════════════════════ */
.pipeline-item { margin-bottom: 16px; }
.pipeline-item:last-child { margin-bottom: 0; }

.pipeline-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
}

.pipeline-stage { font-size: 13px; font-weight: 600; }

.pipeline-count {
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
}

.pipeline-bar-track {
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.pipeline-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.pipeline-value {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 4px;
  text-align: right;
}

/* ══════════════════════════════
   EMPTY STATE
══════════════════════════════ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  gap: 10px;
}

.empty-icon {
  font-size: 32px;
  color: #cbd5e1;
}

.empty-text {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

/* ══════════════════════════════
   ANIMATION
══════════════════════════════ */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>